<script setup>
import FormItem from "../../Registration/components/FormItem.vue";
import {useBookingStore} from "../../Registration/stores/bookingStore";
import {computed, defineAsyncComponent} from "vue";
import {useFlowStore} from "../../Registration/stores/flowStore";
import RegistrationEndMessage from "../../Registration/components/RegistrationEndMessage.vue";
import ParticipationStep from "../../Registration/components/ParticipationStep.vue";
import {useSettingsManager} from "../../Registration/stores/settingsManagerStore";
import FormWarningBanner from "../../Registration/components/FormWarningBanner.vue";
import CompanionsSection from "../../Registration/components/Companions/CompanionsSection.vue";
import FormStepNavigator from "../../Registration/components/MultistepForm/FormStepNavigator.vue";
import {useStepManager} from "../../Registration/stores/stepsManager";
import {useI18n} from "vue-i18n";
import {useCaptchaManager} from "../../Registration/stores/captchaManagerStore";

const Captcha = defineAsyncComponent(()=>import("../../Registration/components/Captcha.vue"));
const booking = useBookingStore();
const flow = useFlowStore();
const stepsManager = useStepManager();
const {settings, event} = useSettingsManager();
const {t} = useI18n();
const resolvedConfirmationStatus = computed(() => {
    const guestAuthenticated = booking.status !== null;
    // status from settings
    const {useDifferentRegistrationStatus, newGuestsStatus, invitedGuestsStatus} = settings;
    if (useDifferentRegistrationStatus) {
        if (!guestAuthenticated) {
            return newGuestsStatus;
        }
        if (booking.status === 'invited') {
            return invitedGuestsStatus;
        }
    }

    return 'confirmed';

});
const handleFormSubmission = async () => {
    booking.startSubmissionLoader();

    const resolveSubmissionStatus = () => {
        const confirmationStatus = resolvedConfirmationStatus.value;
        const {withConfirmation} = settings;
        if (withConfirmation && flow.participationStatus === 'declined') {
            return 'cancelled';
        }
        return confirmationStatus;
    }
    await booking.submit(resolveSubmissionStatus());
}

const submissionButtonClasses = computed(() => {
    const classes = ['btn'];
    if (booking.loading) {
        classes.push('btn-disabled');
    }
    return classes.join(' ');
});

const needsConfirmation = computed(() => {
    const guestAuthenticated = booking.status !== null;
    if (settings?.withConfirmation) {
        if (event?.settings?.registration_type === 'password') {
            return false;
        }
        if (event?.settings?.registration_type === 'open' && !guestAuthenticated) {
            return false;
        }
        if (guestAuthenticated) {
            if (!flow.isFormEditionActive) {
                return false;
            }
        }
        return flow.participationStatus === 'default';
    }
    return false;
});
const shouldDisplayForm = computed(() => {
    if (needsConfirmation.value === true) {
        return false;
    }
    if (flow.participationStatus === 'declined') {
        return false;
    }
    const guestAuthenticated = booking.status !== null;
    const guestStatusDefault = ['invited', 'listed'].includes(booking.status)
    if (guestAuthenticated && !guestStatusDefault) {
        if (!flow.isFormEditionActive) {
            return false;
        }
    }
    return flow.formSubmission === 'default';
})
const shouldDisplayGeneralSubmissionButton = computed(() => {
    if (stepsManager.steps.length >= 1) {
        return false;
    }
    return true;
})
const shouldDisplayFinalStepSubmissionButton = computed(() => {
    if (stepsManager.steps.length >= 1) {
        return stepsManager.canSubmit;
    }
    return false;
})

const showCaptcha = computed(()=>{
    const captcha = useCaptchaManager();
    if(!captcha.required){
        return false;
    }
    if(stepsManager.steps.length > 1){
        return stepsManager.isOnLastStep;
    }
    return true;
})

</script>

<template>
    <ParticipationStep v-if="needsConfirmation"/>

    <form id="registration-form" v-if="shouldDisplayForm" @submit.prevent="handleFormSubmission">
        <FormStepNavigator/>
        <FormWarningBanner/>
        <div class="registration-fields-wrapper grid grid-cols-1 form-fields-grid md:grid-cols-12">
            <FormItem v-for="item in booking.items" :item="item" :key="item.id"/>
        </div>
        <CompanionsSection/>
        <div v-if="showCaptcha">
            <Captcha />
        </div>
        <div id="form-actions-section" class="form-actions">
            <div class="flex justify-center items-center w-full">
                <button @click.stop.prevent="()=>stepsManager.previous()" v-if="stepsManager.canMovePrevious" class="btn back-btn mr-2" :disabled="booking.loading"
                        v-text="t('ui.registration.multi_step_form.back')"></button>
                <button @click.stop.prevent="()=>stepsManager.next()" v-if="stepsManager.canMoveNext" class="btn next-btn mr-2" :disabled="booking.loading"
                        v-text="t('ui.registration.multi_step_form.next')"></button>
                <button data-button="general-submission" v-if="shouldDisplayGeneralSubmissionButton" :class="submissionButtonClasses" :disabled="booking.loading"
                        v-text="settings.submitButtonText"></button>
                <button data-button="multistep-submission" @click.stop.prevent="()=>stepsManager.submit(resolvedConfirmationStatus)" v-if="shouldDisplayFinalStepSubmissionButton"
                        :class="submissionButtonClasses"
                        :disabled="booking.loading"
                        v-text="settings.submitButtonText"></button>
            </div>
        </div>
    </form>
    <RegistrationEndMessage></RegistrationEndMessage>
</template>

<style scoped>

</style>
